/**
 * Every path in the portal is English since build round 34 (2026-10-10, Mattej's decision): only the
 * addresses changed, every word a person reads stays Swedish. Each old Swedish path answers 301 to its
 * English one in the Worker (`worker/src/index.ts`), and the app redirects the same way for a
 * navigation the service worker answers from its cache, so no link that has gone out breaks. The
 * redirects stay for good. New paths are English from now on.
 *
 * Tabs keep their Swedish keys inside the code (`larm`, `konto`, ...), where test ids and logic use
 * them; only the address segment is English, through the maps below. This module is pure, with no
 * imports, because the Worker imports it too.
 */

/** `/settings/:section`. */
export const SETTINGS_SEGMENT = {
  konto: "account",
  sakerhet: "security",
  inloggningar: "sessions",
  notiser: "notifications",
  utseende: "appearance",
  forvaltning: "management",
  personer: "people"
} as const;

/** `/admin/clients/:tenantId/:tab`. */
export const CLIENT_TAB_SEGMENT = {
  oversikt: "overview",
  konversation: "conversation",
  arenden: "requests",
  tjanster: "services",
  personer: "people",
  dokument: "documents",
  drift: "operations",
  avslut: "offboarding"
} as const;

/** `/admin/operations/:tab`. */
export const OPERATIONS_TAB_SEGMENT = {
  larm: "alerts",
  overvakning: "monitoring",
  kopior: "backups",
  gallring: "retention",
  konton: "account-deletion",
  sakerhet: "security",
  arsbevis: "annual-proof",
  avslut: "offboarding"
} as const;

/** `/admin/records/:tab` (Underlag: workload, invoices and the insights kept from clients' messages). */
export const RECORDS_TAB_SEGMENT = {
  arbete: "workload",
  fakturor: "invoices",
  insikter: "insights"
} as const;

/** The key whose English segment this is, or undefined. */
export function keyForSegment<K extends string>(map: Readonly<Record<K, string>>, segment: string | undefined): K | undefined {
  if (segment === undefined) return undefined;
  return (Object.keys(map) as K[]).find((key) => map[key] === segment);
}

/** Old first segments and their English replacements, outside the tabbed ones handled below. */
const RENAMED: Readonly<Record<string, string>> = {
  "/integritet": "/privacy",
  "/rapporter": "/reports",
  "/notiser": "/notifications",
  "/hemlighet": "/secrets",
  "/nytt-losenord": "/new-password",
  "/bekrafta-adress": "/confirm-email",
  "/verifiering": "/verify",
  "/webbplatsens-fakta": "/site-facts",
  "/admin/inkorg": "/admin/inbox",
  "/admin/installningar": "/admin/settings",
  "/admin/notiser": "/admin/notifications"
};

/** Every old Swedish path prefix the portal ever served, for the tests and for the reader. */
export const LEGACY_PREFIXES: readonly string[] = [
  ...Object.keys(RENAMED),
  "/admin/kunder",
  "/admin/drift",
  "/admin/underlag",
  ...Object.keys(SETTINGS_SEGMENT).map((key) => `/settings/${key}`)
];

function under(path: string, prefix: string): string | null {
  if (path === prefix) return "";
  return path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : null;
}

function tabbed<K extends string>(rest: string, map: Readonly<Record<K, string>>): string {
  const [tab, ...more] = rest.split("/").filter(Boolean);
  const english = tab !== undefined && tab in map ? map[tab as K] : tab;
  return [english, ...more].filter((part) => part !== undefined).map((part) => `/${part}`).join("");
}

/**
 * The English path for an old Swedish one, without query or fragment, or null when the path is not
 * an old one. A trailing slash is dropped. Segments after the renamed part are kept as they are.
 */
export function englishPathFor(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  for (const [old, english] of Object.entries(RENAMED)) {
    const rest = under(path, old);
    if (rest !== null) return `${english}${rest}`;
  }

  const settings = /^\/settings\/([^/]+)(\/.*)?$/.exec(path);
  if (settings && settings[1] in SETTINGS_SEGMENT) {
    return `/settings/${SETTINGS_SEGMENT[settings[1] as keyof typeof SETTINGS_SEGMENT]}${settings[2] ?? ""}`;
  }

  const clients = under(path, "/admin/kunder");
  if (clients !== null) {
    const [tenant, ...rest] = clients.split("/").filter(Boolean);
    if (tenant === undefined) return "/admin/clients";
    return `/admin/clients/${tenant}${tabbed(rest.join("/"), CLIENT_TAB_SEGMENT)}`;
  }

  const operations = under(path, "/admin/drift");
  if (operations !== null) return `/admin/operations${tabbed(operations, OPERATIONS_TAB_SEGMENT)}`;

  const records = under(path, "/admin/underlag");
  if (records !== null) return `/admin/records${tabbed(records, RECORDS_TAB_SEGMENT)}`;

  return null;
}

/** The old prefixes the route table sends to `LegacyPathRoute`, each with everything under it. */
export const LEGACY_ROUTE_PATHS = [
  "/integritet",
  "/rapporter/*",
  "/notiser",
  "/hemlighet/*",
  "/nytt-losenord",
  "/bekrafta-adress",
  "/verifiering",
  "/webbplatsens-fakta/*",
  "/admin/kunder/*",
  "/admin/inkorg",
  "/admin/installningar",
  "/admin/notiser",
  "/admin/drift/*",
  "/admin/underlag/*"
] as const;
